import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * Per-user registered emergency address (RAY BAUM §506 + Kari's Law
 * compliance). One row per user — the address dispatch will be
 * routed to if the user dials 911 from any of their assigned
 * softphone numbers.
 *
 * Why per-user (not per-number, not per-tenant):
 *   - Per-tenant ("office address") fails RAY BAUM the moment a rep
 *     dials 911 from home. Non-fixed MLTS (browser softphones) must
 *     carry a current dispatchable location.
 *   - Per-number is wasteful — a rep with 3 DIDs (sales, support,
 *     brand-X) sits at one desk; one address covers all 3.
 *   - Per-user matches industry norm (RingCentral / Vonage / Dialpad
 *     all use this shape) and the MSA can transfer "keep it current"
 *     liability to the tenant cleanly.
 *
 * Lifecycle:
 *   1. User is assigned their first softphone number → admin (or the
 *      user via onboarding wizard) collects an address.
 *   2. Address is validated against USPS via Twilio's Address API
 *      (https://www.twilio.com/docs/usage/api/address). On success
 *      we store the `twilioAddressSid` and stamp `validatedAt`.
 *   3. Twilio EmergencyAddress is registered against EACH of the
 *      user's `SoftphoneNumberEntity` rows, so any number → 911 dial
 *      dispatches to this address.
 *   4. Once a year the lazy expiration check (computed at
 *      outbound-dial time in `SoftphoneCallRoutingService`) rejects
 *      the dial and surfaces a re-attestation modal. The rep either
 *      confirms (bumps `lastAttestedAt`) or re-runs the address
 *      wizard.
 *
 * Block-on-expired is OUTBOUND-DIAL ONLY and explicitly EXCLUDES
 * emergency numbers (911, 933, 988) so a stuck rep can still get
 * help. The registered address is the user's best-known location —
 * better than a hard rejection of the emergency call.
 *
 * When a softphone number is reassigned from user A → user B, the
 * service drops user A's Twilio EmergencyAddress link from that
 * number and registers user B's. The address rows themselves stay —
 * a user keeps their address regardless of which numbers they hold.
 */
@Index('softphone_e911_address_user_uq', ['fkUserId'], {
	unique: true,
	// Soft-deleted rows excluded so a user can have their address
	// row recreated after deletion without colliding on the unique
	// index. Matches the codebase's usual soft-delete + unique
	// pattern.
	where: '`_deleted_date` IS NULL',
})
@Entity('softphone_e911_address')
export class SoftphoneE911AddressEntity extends BaseEntity {
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Optional user-facing label ("Home office", "Detroit branch") so
	 * a rep with multiple known locations can recognise their current
	 * registered address at a glance during re-attestation.
	 */
	@Column('varchar', { name: 'friendlyName', length: 100, nullable: true })
	public friendlyName!: string | null;

	@Column('varchar', { name: 'street1', length: 200 })
	public street1!: string;

	/** Apartment / suite / unit. Stored separately from street1 because
	 *  USPS validation treats them as distinct fields and dispatchers
	 *  need the suite to find the right unit in a multi-tenant building. */
	@Column('varchar', { name: 'street2', length: 200, nullable: true })
	public street2!: string | null;

	@Column('varchar', { name: 'city', length: 100 })
	public city!: string;

	/** US state / province. 2-letter code preferred ('MI', 'CA'); we
	 *  also accept full names and let Twilio's USPS validation
	 *  normalise. */
	@Column('varchar', { name: 'region', length: 50 })
	public region!: string;

	@Column('varchar', { name: 'postalCode', length: 16 })
	public postalCode!: string;

	/** ISO 3166-1 alpha-2 country code. 'US' / 'CA' for v1; other
	 *  countries are not supported in v1 (E911 is US-only; CA has its
	 *  own equivalent we'll need to wire separately). */
	@Column('varchar', { name: 'isoCountry', length: 2, default: 'US' })
	public isoCountry!: string;

	/**
	 * The SID Twilio returns after a successful Address resource
	 * creation. Format: `AD…`. We use this to attach the address to
	 * each of the user's softphone numbers via the Twilio
	 * IncomingPhoneNumber.update() with `emergencyAddressSid=…`.
	 *
	 * Null when the address row exists but hasn't been validated yet
	 * (e.g. mid-onboarding-wizard, between collection and
	 * validation). The dial-gate refuses outbound when this is null
	 * just as if `validatedAt` were null — unvalidated is the same as
	 * expired for routing purposes.
	 */
	@Column('varchar', { name: 'twilioAddressSid', length: 64, nullable: true })
	public twilioAddressSid!: string | null;

	/**
	 * When the USPS validation last succeeded against Twilio's
	 * Address API. Distinct from `lastAttestedAt`: validation is the
	 * heavy round-trip ("does USPS recognise this address?");
	 * attestation is the light click ("yes, it's still where I am").
	 *
	 * Re-validation only happens on "I moved" (re-run wizard).
	 * Re-attestation happens once a year OR when the rep
	 * explicitly confirms in the modal.
	 */
	@Column('datetime', { name: 'validatedAt', nullable: true })
	public validatedAt!: Date | null;

	/**
	 * When the rep last attested ("yes, this is still where I am").
	 * Starts equal to `validatedAt` on first save. The annual
	 * outbound-dial gate compares against this column.
	 *
	 * Null is treated as "expired" — block outbound (except
	 * emergency) until the rep attests.
	 */
	@Column('datetime', { name: 'lastAttestedAt', nullable: true })
	public lastAttestedAt!: Date | null;

	/**
	 * PD-14744 — who performed the last attestation. Self-service
	 * paths record the rep themself; the admin set-on-behalf paths
	 * record the acting admin (per compliance decision: an admin
	 * setting an address on a rep's behalf counts as attestation, with
	 * the admin as the attester of record).
	 *
	 * Tenant-DB user id when the attester is a tenant-side user (the
	 * rep, or a CRM admin). Null for config-app sets — Workd config
	 * admins have no user row in the tenant DB; `lastAttestedByEmail`
	 * carries their identity instead.
	 *
	 * Intentionally a plain column (no FK constraint / relation): the
	 * attester is an audit-trail pointer, and a later deletion of that
	 * user must neither cascade into nor block this compliance row.
	 */
	@Column('int', {
		name: 'fk_user__id_attestedBy',
		unsigned: true,
		nullable: true,
	})
	public fkUserIdAttestedBy!: number | null;

	/**
	 * PD-14744 — human-readable identity of the last attester, set on
	 * every attestation path (rep self-service, CRM admin
	 * set-on-behalf, config-app set-on-behalf). The uniform audit
	 * answer to "who attested this address?" regardless of whether the
	 * attester exists in the tenant DB.
	 */
	@Column('varchar', {
		name: 'lastAttestedByEmail',
		length: 255,
		nullable: true,
	})
	public lastAttestedByEmail!: string | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
